import * as z from "zod";
import { create } from "zustand";

import { isSelfHostedDistribution } from "utils/distribution";

import { ADVANCE_NOTICE_DAYS, STATUS_PAGE_URL, UPCOMING_MAINTENANCE_URL } from "./constants";

const ScheduledMaintenance = z.object({
  id: z.string(),
  name: z.string(),
  scheduled_for: z.string(),
  shortlink: z.string().optional(),
  updated_at: z.string().optional(),
  incident_updates: z.array(z.object({ body: z.string() })).default([]),
});

const UpcomingMaintenances = z.object({
  scheduled_maintenances: z.array(ScheduledMaintenance),
});

type ScheduledMaintenance = z.infer<typeof ScheduledMaintenance>;

type ScheduledMaintenanceNotice = {
  id: string;
  title: string;
  body: string;
  url: string;
  /** Statuspage's own last-edited stamp - dismissing a notice only hides that version of it. */
  updatedAt: string;
};

type ScheduledMaintenanceState = {
  notice?: ScheduledMaintenanceNotice;
  hasFetched: boolean;
};

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/** Statuspage keeps a window in `upcoming` until someone marks it in progress, so past ones can appear. */
const pickNextNotice = (
  maintenances: ScheduledMaintenance[]
): ScheduledMaintenanceNotice | undefined => {
  const nowMs = Date.now();
  const noticeWindowEnd = nowMs + ADVANCE_NOTICE_DAYS * MILLISECONDS_PER_DAY;

  const next = maintenances
    .map((maintenance) => ({ ...maintenance, startsAt: Date.parse(maintenance.scheduled_for) }))
    .filter(({ startsAt }) => startsAt > nowMs && startsAt <= noticeWindowEnd)
    .sort((a, b) => a.startsAt - b.startsAt)[0];

  if (!next) return undefined;

  return {
    id: next.id,
    title: next.name,
    body: next.incident_updates[0]?.body ?? "",
    url: next.shortlink ?? STATUS_PAGE_URL,
    updatedAt: next.updated_at ?? "",
  };
};

const useScheduledMaintenanceStore = create<ScheduledMaintenanceState>(() => ({
  notice: undefined,
  hasFetched: false,
}));

export const fetchScheduledMaintenance = async () => {
  if (useScheduledMaintenanceStore.getState().hasFetched || isSelfHostedDistribution()) return;

  // Flagged before the request so concurrent consumers don't duplicate it. A failure is not
  // retried - one silent attempt per session is enough for an advance notice.
  useScheduledMaintenanceStore.setState({ hasFetched: true });

  try {
    const response = await fetch(UPCOMING_MAINTENANCE_URL);
    const upcoming = UpcomingMaintenances.parse(await response.json());

    useScheduledMaintenanceStore.setState({
      notice: pickNextNotice(upcoming.scheduled_maintenances),
    });
  } catch {
    // No advance notice is better than a broken one.
  }
};

export default useScheduledMaintenanceStore;
